import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import { ToastContext } from './toast.context';
import type { ToastOptions } from './toast.types';

interface ToastInternal extends ToastOptions {
  id: string;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastInternal[]>([]);

  const removeToast = React.useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const toast = React.useCallback(
    ({ id, duration = 4000, ...options }: ToastOptions) => {
      const toastId = id ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const next: ToastInternal = {
        id: toastId,
        duration,
        variant: options.variant ?? 'default',
        title: options.title,
        description: options.description,
      };

      setToasts((current) => [...current, next]);

      window.setTimeout(() => removeToast(toastId), duration);
    },
    [removeToast]
  );

  const value = React.useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

function Toaster({
  toasts,
  onDismiss,
}: {
  toasts: ToastInternal[];
  onDismiss: (id: string) => void;
}) {
  if (!toasts.length) return null;

  return (
    <div className="fixed left-1/2 -translate-x-1/2 z-[2000] flex flex-col-reverse gap-2 p-4 pointer-events-none" style={{ bottom: '2rem' }}>
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'pointer-events-auto relative flex w-full max-w-[90vw] items-center gap-3 overflow-hidden rounded-lg bg-black/90 text-white px-4 py-2 shadow-lg',
            toast.variant === 'success' && 'border-green-500',
            toast.variant === 'destructive' && 'border-red-500'
          )}
          role="status"
        >
          <div className="flex-1 min-w-0">
            {toast.description && (
              <p className="text-xs text-white/90 whitespace-nowrap overflow-hidden text-ellipsis" style={{maxWidth: 'calc(90vw - 2.5rem)'}}>{toast.description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onDismiss(toast.id)}
            className="ml-2 text-white/70 hover:text-white"
          >
            <X className="h-3 w-3" aria-hidden="true" />
            <span className="sr-only">Close</span>
          </button>
        </div>
      ))}
    </div>
  );
}

